import React from 'react';
import { Link } from 'react-router-dom';

/**
 * What a visitor sees at an address that does not exist.
 *
 * One component for all three places this happens — a page that was never
 * published, a shop on a brand that does not sell, an address typed wrong —
 * because three near-identical screens is how they drift apart, and the brand
 * can now word it themselves.
 *
 * The defaults avoid saying anything about the site's internals. "This page
 * hasn't been published yet" told a customer about the owner's workflow; they
 * only need to know there is nothing here and how to get somewhere useful.
 */

const DEFAULTS = {
  title: "We couldn't find that page.",
  message: 'It may have moved, or the address may be slightly off.',
  buttonLabel: 'Back to the homepage',
  buttonUrl: '/',
};

export default function NotFoundBody({ notFound, homePath = '/' }) {
  const cfg = notFound?.enabled ? notFound : {};
  const title = cfg.title || DEFAULTS.title;
  const message = cfg.message || DEFAULTS.message;
  const buttonLabel = cfg.buttonLabel || DEFAULTS.buttonLabel;
  const buttonUrl = cfg.buttonUrl || homePath || DEFAULTS.buttonUrl;

  const external = /^https?:\/\//i.test(buttonUrl);

  const buttonStyle = {
    color: '#4F46E5',
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: 600,
    border: '1px solid currentColor',
    borderRadius: 10,
    padding: '10px 18px',
    display: 'inline-block',
  };

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 20px',
        textAlign: 'center',
        fontFamily: "'Inter', -apple-system, system-ui, sans-serif",
        color: '#6b7280',
      }}
    >
      {/* The brand's own picture replaces the magnifying glass rather than
          sitting above it — two illustrations stacked is not a considered
          screen, and the emoji only exists because most brands never set
          one. */}
      {cfg.image ? (
        <img
          src={cfg.image}
          alt=''
          style={{
            maxWidth: 'min(320px, 80%)',
            height: 'auto',
            marginBottom: 20,
            borderRadius: 12,
          }}
        />
      ) : (
        <div style={{ fontSize: 48, marginBottom: 14 }}>🔍</div>
      )}
      <div style={{ fontSize: 19, fontWeight: 600, marginBottom: 6, color: '#111827' }}>
        {title}
      </div>
      {message && <div style={{ fontSize: 14, marginBottom: 24, maxWidth: 460 }}>{message}</div>}
      {buttonLabel && (
        external
          ? <a href={buttonUrl} style={buttonStyle} rel='noopener noreferrer'>{buttonLabel}</a>
          : <Link to={buttonUrl} style={buttonStyle}>{buttonLabel}</Link>
      )}
    </div>
  );
}
